import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getApiError } from "../../services/courseApi";
import { listCoursePayments, listUsers } from "../../services/coursePaymentApi";
import { displayDate, fullName, money, userMap } from "./coursePaymentUtils";
import "./coursePayment.css";

const statuses = ["SUBMITTED", "VERIFIED", "REJECTED", "UNPAID"];

export default function CoursePaymentList() {
  const [status, setStatus] = useState("SUBMITTED");
  const [search, setSearch] = useState("");
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([listCoursePayments({ status, limit: 100 }), listUsers()])
      .then(([paymentResult, userResult]) => {
        if (!active) return;
        setPayments(paymentResult.payments);
        setUsers(userResult);
      })
      .catch((error) => toast.error(getApiError(error, "Could not load course payments")))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [status]);

  const students = useMemo(() => userMap(users), [users]);
  const visiblePayments = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return payments;
    return payments.filter((item) => {
      const student = students.get(Number(item.student_id));
      return [fullName(student), student?.email, item.course?.title, item.provider?.name,
        item.payment?.offline_reference]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [payments, search, students]);

  return (
    <main className="course-page payment-page">
      <div className="course-page-header">
        <div>
          <p className="course-eyebrow">Operations</p>
          <h1>Course payments</h1>
          <p className="text-muted mb-0">Review offline payments and activate course enrollments.</p>
        </div>
      </div>

      <section className="payment-toolbar">
        <div className="payment-tabs" role="tablist" aria-label="Payment status">
          {statuses.map((item) => (
            <button key={item} type="button" className={status === item ? "active" : ""}
              onClick={() => setStatus(item)}>
              {item.charAt(0) + item.slice(1).toLowerCase()}
            </button>
          ))}
        </div>
        <label className="payment-search">
          <i className="bi bi-search" />
          <input value={search} onChange={(event) => setSearch(event.target.value)}
            placeholder="Student, course, provider or reference" aria-label="Search payments" />
        </label>
      </section>

      <section className="course-table-card" aria-busy={loading}>
        {loading ? (
          <div className="course-state"><div className="spinner-border text-primary" /></div>
        ) : visiblePayments.length === 0 ? (
          <div className="course-state">
            <i className="bi bi-receipt fs-1 text-muted" />
            <h2>No {status.toLowerCase()} payments</h2>
            <p className="text-muted">Payments matching this status will appear here.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table course-table align-middle mb-0">
              <thead><tr><th>Student</th><th>Course</th><th>Provider</th><th>Amount</th>
                <th>Submitted</th><th>Status</th><th className="text-end">Action</th></tr></thead>
              <tbody>{visiblePayments.map((item) => {
                const student = students.get(Number(item.student_id));
                return <tr key={item.payment?.payment_id || item.enrollment_id}>
                  <td><strong>{fullName(student)}</strong><div className="small text-muted">
                    {student?.email || `Student #${item.student_id}`}</div></td>
                  <td><div>{item.course?.title || "—"}</div><div className="small text-muted">
                    Enrollment #{item.enrollment_id}</div></td>
                  <td><div>{item.provider?.name || "—"}</div><div className="small text-muted text-capitalize">
                    {String(item.provider?.type || "").toLowerCase()}</div></td>
                  <td>{money(item.payment?.amount, item.payment?.currency)}</td>
                  <td>{displayDate(item.payment?.submitted_at, true)}</td>
                  <td><span className={`payment-status ${String(item.payment?.status).toLowerCase()}`}>
                    {item.payment?.status}</span></td>
                  <td className="text-end"><Link className="btn btn-sm btn-outline-primary"
                    to={`/course-payments/${item.payment?.payment_id}`}>Review</Link></td>
                </tr>;
              })}</tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
